import React, { useEffect, useRef, useState } from 'react';

/**
 * Google AdSense — Homepage-only integration.
 *
 * - Loads the official adsbygoogle.js script exactly ONCE (async), only when a
 *   publisher ID is configured and ads are enabled.
 * - Renders real <ins class="adsbygoogle"> units; no simulated/placeholder ads.
 * - Each unit pushes to the ad queue once per mount (ref-guarded) so React
 *   re-renders never fire duplicate ad requests.
 * - Ad-blockers, network failures and unfilled inventory are swallowed so the
 *   Homepage never breaks. Unfilled units collapse via CSS (see index.css).
 */

let scriptRequested = false;

export function loadAdSenseScript(publisherId) {
  if (!publisherId || scriptRequested) return;
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-adsense-loader="1"]')) {
    scriptRequested = true;
    return;
  }
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(publisherId)}`;
    s.crossOrigin = 'anonymous';
    s.setAttribute('data-adsense-loader', '1');
    s.onerror = () => { /* ad-blocker / offline — never break the page */ };
    document.head.appendChild(s);
    scriptRequested = true;
  } catch (_) { /* no-op */ }
}

/** A single responsive AdSense display unit. Renders nothing without client+slot. */
export const AdUnit = ({ client, slot, testId, style }) => {
  const pushed = useRef(false);
  useEffect(() => {
    if (pushed.current || !client || !slot) return;
    const t = setTimeout(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (_) { /* ad not ready / blocked — ignore */ }
    }, 0);
    return () => clearTimeout(t);
  }, [client, slot]);

  if (!client || !slot) return null;
  return (
    <ins
      className="adsbygoogle"
      style={style || { display: 'block', width: '100%' }}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format="auto"
      data-full-width-responsive="true"
      data-testid={testId}
    />
  );
};

const AdLabel = () => (
  <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-semibold mb-1.5 text-center select-none">
    Advertisement
  </div>
);

/**
 * A full-width ad row inserted into the Homepage listing grid.
 * - cols === 4 (desktop): up to two units side-by-side; a missing/unfilled unit
 *   collapses without leaving excess space.
 * - cols === 2 (mobile/tablet): one full-width responsive unit.
 * Spans the full grid width at every breakpoint (col-span-2 lg:col-span-4).
 */
export const HomeAdRow = ({ cfg, cols, position }) => {
  if (!cfg || !cfg.enabled || !cfg.publisher_id) return null;

  if (cols >= 4) {
    if (!cfg.placement_desktop_enabled) return null;
    const s1 = cfg.slot_desktop_1;
    const s2 = cfg.slot_desktop_2;
    if (!s1 && !s2) return null;
    return (
      <div className="col-span-2 lg:col-span-4 my-2" data-testid={`home-ad-desktop-${position}`}>
        <AdLabel />
        <div className="flex gap-5 items-stretch justify-center">
          {s1 && (
            <div className="flex-1 min-w-0">
              <AdUnit client={cfg.publisher_id} slot={s1} testId={`adslot-desktop1-${position}`} />
            </div>
          )}
          {s2 && (
            <div className="flex-1 min-w-0">
              <AdUnit client={cfg.publisher_id} slot={s2} testId={`adslot-desktop2-${position}`} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!cfg.placement_mobile_enabled || !cfg.slot_mobile) return null;
  return (
    <div className="col-span-2 my-2" data-testid={`home-ad-mobile-${position}`}>
      <AdLabel />
      <AdUnit client={cfg.publisher_id} slot={cfg.slot_mobile} testId={`adslot-mobile-${position}`} />
    </div>
  );
};

/** Responsive grid-column hook: 4 columns on lg (>=1024px), else 2. */
export function useGridCols() {
  const getCols = () => (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 4 : 2);
  const [cols, setCols] = useState(getCols);
  useEffect(() => {
    const onResize = () => setCols(getCols());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return cols;
}

/**
 * Should an ad row be inserted AFTER the card at `index` (0-based)?
 * Frequency is driven by the actual grid columns: every 8 cards on the 4-col
 * desktop grid, every 4 cards on the 2-col mobile/tablet grid. Ads are never
 * counted as listings — placement is computed purely from the card index.
 */
export function shouldInsertAdAfter(index, cols, cfg) {
  if (!cfg || !cfg.enabled || !cfg.publisher_id) return false;
  const freq = cols >= 4 ? (cfg.frequency_desktop || 8) : (cfg.frequency_mobile || 4);
  if (!freq || freq < 1) return false;
  return (index + 1) % freq === 0;
}
